import { z } from 'zod';
import { paginatedSchema, paginationQuerySchema } from './api-error.js';
import { sortQuerySchema } from './sort.js';
import { checkStatusSchema } from './check.js';
import { slugSchema } from './client.js';

/**
 * Default confirmation threshold. A canary that takes longer than this to show up
 * in Mailgun is reported as a warning rather than a pass.
 *
 * @since 1.0.0
 */
export const DEFAULT_CONFIRM_THRESHOLD_MS = 60_000;

/**
 * How the canary drives one field of a form. `value` is literal text; `qcRunId`
 * and `qcEmail` are substituted at run time, so a test never types a stranger's address.
 *
 * @since 1.0.0
 */
export const formFieldSchema = z.object({
    selector: z.string().min(1),
    kind: z.enum(['text', 'email', 'textarea', 'select', 'checkbox']).default('text'),
    value: z.string().default(''),
    substitute: z.enum(['none', 'qc-run-id', 'qc-email']).default('none'),
});

export type FormField = z.infer<typeof formFieldSchema>;

/**
 * Everything the canary needs to fill and submit one form without any per-client
 * code: field map, submit button, and what success looks like.
 *
 * @since 1.0.0
 */
export const formSelectorsSchema = z.object({
    fields: z.array(formFieldSchema).default([]),
    submitSelector: z.string().default('button[type="submit"]'),
    successSelector: z.string().default(''),
    successText: z.string().default(''),
});

export type FormSelectors = z.infer<typeof formSelectorsSchema>;

export const formSchema = z.object({
    id: z.number().int(),
    clientId: z.number().int(),
    clientSlug: slugSchema,
    slug: slugSchema,
    name: z.string(),
    pagePath: z.string(),
    isCanaryEnabled: z.boolean(),
    qcRecipient: z.email(),
    notificationSubjectPattern: z.string().nullable(),
    expectedRecipients: z.array(z.email()),
    selectors: formSelectorsSchema,
    thresholdMs: z.number().int(),
    lastCanaryRunId: z.string().nullable(),
    createdAt: z.number().int(),
});

export type Form = z.infer<typeof formSchema>;

export const createFormRequestSchema = z.object({
    clientSlug: slugSchema,
    slug: slugSchema,
    name: z.string().min(1).max(160),
    pagePath: z.string().min(1).max(512),
    isCanaryEnabled: z.boolean().default(false),
    qcRecipient: z.email().optional(),
    notificationSubjectPattern: z.string().max(255).nullable().default(null),
    expectedRecipients: z.array(z.email()).default([]),
    selectors: formSelectorsSchema.optional(),
    thresholdMs: z.number().int().min(1000).max(600_000).default(DEFAULT_CONFIRM_THRESHOLD_MS),
});

export type CreateFormRequest = z.infer<typeof createFormRequestSchema>;

export const updateFormRequestSchema = createFormRequestSchema.partial().omit({ clientSlug: true, slug: true });

export type UpdateFormRequest = z.infer<typeof updateFormRequestSchema>;

/**
 * A row of the Forms view: registration plus the outcome of its most recent canary.
 *
 * @since 1.0.0
 */
export const formRowSchema = formSchema.extend({
    clientName: z.string(),
    lastRun: z
        .object({
            qcRunId: z.string(),
            status: checkStatusSchema,
            summary: z.string(),
            correlationMs: z.number().int().nullable(),
            finishedAt: z.number().int().nullable(),
        })
        .nullable(),
});

export type FormRow = z.infer<typeof formRowSchema>;

/**
 * One page of the Forms view.
 *
 * @since 1.3.0
 */
export const formRowsPageSchema = paginatedSchema(formRowSchema);

export type FormRowsPage = z.infer<typeof formRowsPageSchema>;

/**
 * Columns the Forms view may be sorted on, named after the table's own column keys.
 *
 * @since 1.4.0
 */
export const FORM_SORT_KEYS = [
    'client',
    'name',
    'page',
    'canary',
    'last-run',
    'confirm-time',
    'status',
    'outcome',
] as const;

export const formSortKeySchema = z.enum(FORM_SORT_KEYS);

export type FormSortKey = z.infer<typeof formSortKeySchema>;

export const formQuerySchema = paginationQuerySchema
    .extend(sortQuerySchema(FORM_SORT_KEYS, 'client', 'asc').shape)
    .extend({
        client: slugSchema.optional(),
    });

export type FormQuery = z.infer<typeof formQuerySchema>;
